import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import branch from 'client/core/branch';

import firstWordCapitalized from 'common/util/firstWordCapitalized';

import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_LOCKBOX_TYPE_LOCKBOX,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

import {
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_SHOWING_STATUS_COMPLETED,
} from 'common/tourConstants';

import SrAgentAddListingToTourButton from 'client/components/SrAgentAddListingToTourButton';
import SrAgentWantsToApplyButton from 'client/components/SrAgentWantsToApplyButton';
import SrAgentTourListingQuestions from 'client/components/SrAgentTourListingQuestions';
import ButtonDeprecated, { BUTTON_THEME_RED_INVERT, BUTTON_THEME_GREEN_INVERT } from 'client/components/ButtonDeprecated';

import AgentActions from 'client/actions/Agent';
import TourActions from 'client/actions/Tour';

@branch({
  agentTours: ['agent', 'tours'],
  agentUserId: ['currentUser', 'id'],
})
class SrListingTourAgentSection extends Component {
  static propTypes = {
    agentUserId: PropTypes.number.isRequired,
    agentTourId: PropTypes.number.isRequired,
    listing: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    agentTours: PropTypes.array,
    onClickScrollDownButton: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  constructor() {
    super();

    this.state = {
      showScrollDownButton: true,
      buttonsDisabled: true,
    };
  }

  componentDidMount() {
    global.addEventListener('scroll', this._onScroll);
  }

  componentWillUnmount() {
    global.removeEventListener('scroll', this._onScroll);
  }

  _setButtonsDisabled = (buttonsDisabled) => {
    this.setState({ buttonsDisabled });
  }

  _onScroll = () => {
    const scrollTop = global.pageYOffset || global.document.documentElement.scrollTop;
    const pageHeight = document.documentElement.scrollHeight;
    const percentage = (scrollTop * 100) / pageHeight;
    const showScrollDownButton = percentage < 50;

    if (showScrollDownButton !== this.state.showTourAgentScrollButton) {
      this.setState({
        showScrollDownButton,
      });
    }
  }

  _loadAgentTours = () => {
    const { agentUserId, dispatch } = this.props;
    dispatch(AgentActions.getAgentTours, {
      agentUserId,
    });
  }

  _completeShowing = (showing, confirmAction = true) => {
    if (confirmAction && !global.confirm('Are you sure you want to complete this showing?')) {
      return;
    }

    const { dispatch, history, agentUserId } = this.props;
    dispatch(TourActions.updateShowingStatus, {
      listingId: showing.listingId,
      tourId: showing.tourId,
      showingStatus: TOUR_SHOWING_STATUS_COMPLETED,
    }).then(() => {
      return dispatch(AgentActions.getAgentTours, {
        agentUserId,
        resetViewingTour: true,
      });
    }).then(() => history.push('/agent/tours'));
  }

  _onFailedShowing = (showing) => {
    const { history } = this.props;
    history.push(`/agent/tours/${showing.tourId}/${showing.listingId}/showing-failed`);
  }

  _onWantsToApply = (wantsToApply) => {
    if (!wantsToApply) {
      this._loadAgentTours();
      return;
    }

    const { agentTours, agentTourId, listing } = this.props;
    const tour = agentTours.find(({ id }) => id === agentTourId);
    const showing = tour.showings.find(({ listingId }) => listingId === listing.id);

    this._completeShowing(showing, false);
  }

  renderAddToTour = (tour) => {
    const { listing } = this.props;

    const showing = tour.showings.find(({ listingId }) => listingId === listing.id);
    if (showing) {
      return;
    }

    const user = tour.users[0];
    const userFirstName = firstWordCapitalized(user.name);
    const isVacant = listing.occupantType === LISTING_OCCUPANT_TYPE_VACANT;
    const isLockbox = listing.lockboxType === LISTING_LOCKBOX_TYPE_LOCKBOX;
    const isSmartApartment = listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT;

    const explainerText = isSmartApartment
      ? 'Cannot add this apartment to the tour. Please contact Sunroom Support!'
      : `Add this ${listing.propertyType.toLowerCase()} and extend ${userFirstName}'s tour right now.`;

    return (
      <div className='section titled agentSection'>
        <div className='titleContainer'>
          <span className='title'>{`add to ${userFirstName.toLowerCase()}'s tour`}</span>
        </div>
        <div className='explainer add'>
          <span>
            {explainerText}
          </span>
        </div>
        <SrAgentAddListingToTourButton
          disabled={isSmartApartment}
        />
        <div className='showingDetailContainer'>
          {listing.occupantType && (
            <span className={classNames('showingDetail occupantType', { vacant: isVacant })}>
              {listing.occupantType}
            </span>
          )}
          {listing.lockboxType && (
            <span className={classNames('showingDetail', { lockbox: isLockbox })}>
              {listing.lockboxType}
            </span>
          )}
        </div>
        {listing.mlsNumber && (
          <div className='mlsNumber'>
            <span>MLS# {listing.mlsNumber}</span>
          </div>
        )}
      </div>
    );
  }

  renderAgentAction = (tour) => {
    const { listing, onClickScrollDownButton, dispatch } = this.props;
    const { showScrollDownButton, buttonsDisabled } = this.state;

    const showing = tour.showings.find(({ listingId }) => listingId === listing.id);
    if (!showing) {
      return null;
    }

    return (
      <div>
        {showScrollDownButton && (
          <span
            className='scrollDown'
            onClick={onClickScrollDownButton}
          >
            scroll down to questions
          </span>
        )}

        <div>
          <SrAgentTourListingQuestions
            tourId={tour.id}
            listingId={listing.id}
            setButtonsDisabled={this._setButtonsDisabled}
          />
        </div>
        <div className='footerSection'>
          <SrAgentWantsToApplyButton
            userId={tour.userId}
            listingId={listing.id}
            showing={showing}
            dispatch={dispatch}
            disabled={buttonsDisabled}
            onWantsToApply={this._onWantsToApply}
          />

          <div className='showingActions'>
            <ButtonDeprecated
              className={classNames({ active: showing.status === TOUR_SHOWING_STATUS_FAILED })}
              theme={BUTTON_THEME_RED_INVERT}
              onClick={() => this._onFailedShowing(showing)}
            >
              <span className='text'>failed</span>
            </ButtonDeprecated>
            <ButtonDeprecated
              className={classNames({ active: showing.status === TOUR_SHOWING_STATUS_COMPLETED })}
              theme={BUTTON_THEME_GREEN_INVERT}
              onClick={() => this._completeShowing(showing)}
              disabled={buttonsDisabled}
            >
              <span className='text'>success</span>
            </ButtonDeprecated>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { agentTours, agentTourId } = this.props;
    const tour = (agentTours || []).find(({ id }) => id === agentTourId);
    if (!tour) {
      return null;
    }

    return (
      <div className='tourAgentSection'>
        {this.renderAddToTour(tour)}
        {this.renderAgentAction(tour)}
      </div>
    );
  }
}

SrListingTourAgentSection.displayName = 'SrListingTourAgentSection';

export default SrListingTourAgentSection;
